import {
  FaroIconButton,
  FaroMenu,
  FaroMenuItem,
  grey,
  red,
  ThreeDotsIcon,
} from "@faro-lotv/flat-ui";
import { Divider } from "@mui/material";
import { useRef, useState, type JSX } from "react";
import type { AnnotationViewerProps } from "./annotation-viewer-types";

type AnnotationMenuProps = Pick<
  AnnotationViewerProps,
  | "canEdit"
  | "canDelete"
  | "onDelete"
  | "onEdit"
  | "onCopyToClipboard"
  | "onContextMenuOpen"
  | "markupCoordinateString"
  | "onCopyMarkupCoordinates"
>;

/**
 * @returns Three dots menu allowing user to trigger different actions (editing, deleting, etc.)
 */
export function AnnotationViewerMenu({
  markupCoordinateString,
  canEdit,
  canDelete,
  onEdit,
  onDelete,
  onCopyToClipboard,
  onContextMenuOpen,
  onCopyMarkupCoordinates,
}: AnnotationMenuProps): JSX.Element {
  const [isOpen, setIsOpen] = useState(false);
  const button = useRef<HTMLButtonElement>(null);

  return (
    <>
      <FaroIconButton
        size="s"
        margin={0}
        dark
        ref={button}
        aria-label="annotation menu"
        onClick={(e) => {
          e.stopPropagation();
          onContextMenuOpen?.();
          setIsOpen(true);
        }}
      >
        <ThreeDotsIcon />
      </FaroIconButton>
      <FaroMenu
        dark
        anchorEl={button.current}
        open={isOpen}
        onClose={() => setIsOpen(false)}
        autoFocus={false}
      >
        {markupCoordinateString && (
          <FaroMenuItem
            dark
            onClick={onCopyMarkupCoordinates}
            label={markupCoordinateString}
          />
        )}
        {markupCoordinateString &&
          (!!onCopyToClipboard || canEdit || canDelete) && <Divider />}

        {onCopyToClipboard && (
          <FaroMenuItem
            onClick={(e) => {
              onCopyToClipboard();
              setIsOpen(false);
              e.stopPropagation();
            }}
            color={grey[0]}
            label="Copy to clipboard"
          />
        )}
        {onCopyToClipboard && (canEdit || canDelete) && <Divider />}
        {canEdit && (
          <FaroMenuItem
            onClick={() => {
              onEdit();
              setIsOpen(false);
            }}
            color={grey[0]}
            label="Edit"
          />
        )}
        {canEdit && canDelete && <Divider />}
        {canDelete && (
          <FaroMenuItem
            onClick={() => {
              onDelete();
              setIsOpen(false);
            }}
            color={red[300]}
            highlightColor={red[300]}
            label="Delete"
          />
        )}
      </FaroMenu>
    </>
  );
}
